import { DateTimePicker } from '@mantine/dates'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { AcknowledgeModal, ActionModal, Flex, FormField, Icon, Modal, Typography } from '@percent/lemonade'
import { useAuthState } from '@percent/partner-dashboard/common/hooks'
import { useServices } from '@percent/partner-dashboard/context/serviceContext/ServiceContext'
import { useMutation } from '@percent/utility'

type ExpireKeySetModalProps = {
  keySetId: string
  keySetName: string
  open: boolean
  onClose: () => void
  refetchApiKeys: () => void
}

export function ExpireKeySetModal({ keySetId, keySetName, open, onClose, refetchApiKeys }: ExpireKeySetModalProps) {
  const { t } = useTranslation()
  const [date, setDate] = useState<Date>(new Date())
  const [status, setStatus] = useState<'action' | 'success' | 'error'>('action')
  const { partnerService } = useServices()
  const { partner } = useAuthState()

  const [, { apiFunc }] = useMutation(
    partnerService.editKeySet,
    () => {
      refetchApiKeys()
      setStatus('success')
    },
    () => {
      setStatus('error')
    }
  )

  const handleSubmit = () => {
    if (partner?.id) {
      apiFunc({ partnerId: partner.id, keySetId, payload: { expiresAt: date?.toISOString() || undefined } })
    }
  }

  return (
    <Modal open={open} onClose={onClose}>
      {status === 'action' && (
        <ActionModal
          title={t('typography.expireKeySet')}
          primaryButtonText={t('typography.expireKeySet')}
          primaryBtnTestId="expire-key-set-btn"
          secondaryButtonText={t('button.cancel')}
          secondaryBtnTestId="cancel-expire-key-set-btn"
          handleClose={onClose}
          handleSubmit={handleSubmit}
        >
          <Flex direction="column" gap={16}>
            <Typography variant="default" $color="gray400">
              {t('keyList.expireKeySet.description', { keySetName })}
            </Typography>
            <FormField label={t('typography.date')} necessity="required">
              <DateTimePicker
                leftSection={<Icon name="calendar" size={6} color="gray600" />}
                value={date}
                wrapperProps={{ 'data-testid': 'expire-key-set-date-picker' }}
                onChange={d => d && setDate(d)}
                popoverProps={{ withinPortal: false }}
                minDate={new Date()}
              />
            </FormField>
          </Flex>
        </ActionModal>
      )}
      {status === 'success' && (
        <AcknowledgeModal
          title={t('keyList.expireKeySet.success.title')}
          result="positive"
          handleClose={onClose}
          buttonText={t('button.close')}
          description={t('keyList.expireKeySet.success.description', {
            keySetName,
            expiresAt: date.toLocaleString()
          })}
        />
      )}
      {status === 'error' && (
        <AcknowledgeModal
          result="negative"
          title={t('keyList.expireKeySet.error.title')}
          handleClose={onClose}
          description={t('keyList.expireKeySet.error.description', { keySetName })}
          buttonText={t('button.close')}
        />
      )}
    </Modal>
  )
}
